'use client';

import Image from 'next/image'; // ALLOW-RAW-IMG: static Morfi wordmark + prize-pool trophy art + podium medals, not user content
import { useTranslations } from 'next-intl';
import type * as React from 'react';

import { UserAvatar } from '@/components/images/user-avatar';
import { LOGO_SRC_DARK_BACKGROUND, logoWordmarkWidthForHeight } from '@/components/logo/constants';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import { Icon } from '@/components/ui/icon';
import { ArrowUpRight01Icon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import { RankEmblem } from './rank-emblem';
import { SHARE_ORIGIN } from './share-rank';

interface Me {
  points: { current: string; lifetime: string; eligible: string };
  rank: number | null;
  level: {
    key: string;
    name: string;
    next: { key: string; name: string } | null;
    toNext: string;
    pct: number;
  } | null;
  streak: { current: number };
  account: { created: boolean; identity: string | null };
  xHandle: string | null;
  xName: string | null;
  avatar: string | null;
  frozen: boolean;
}

interface ProfileCardProps {
  me: Me | null | undefined;
  onCreateAccount: () => void;
  onOpenLevelLadder: () => void;
  creating?: boolean;
}

// Morfi wordmark height inside the card header (white variant over the scrim).
const LOGO_HEIGHT_PX = 24;

export function ProfileCard({
  me,
  onCreateAccount,
  onOpenLevelLadder,
  creating = false,
}: ProfileCardProps): React.ReactNode {
  const t = useTranslations('share.rewards_rank');
  const tr = useTranslations('rewards');
  if (!me) return null;

  // Share target is the crawlable `/rewards` page; the rewards-rank
  // query params make its OG metadata unfurl this participant's standing
  // (Case-B piggy-back — see lib/seo/surfaces/rewards.ts).
  const shareUrl = ((): string => {
    const u = new URL('/rewards', SHARE_ORIGIN);
    if (me.rank !== null && me.rank > 0) {
      u.searchParams.set('rrank', String(me.rank));
      u.searchParams.set('rpts', me.points.current);
      u.searchParams.set('rh', me.xHandle ?? 'anon');
      u.searchParams.set('rlvl', me.level?.key ?? 'spectator');
      u.searchParams.set('rname', me.level?.name ?? '');
    }
    return u.toString();
  })();

  const handle = me.xHandle ?? 'anon';
  // X display name when connected; otherwise the @handle stands in as the
  // primary identity and the secondary handle line is dropped (no echo).
  // `typeof` guard (not `!== null`): a persisted/SSR cache row that predates the
  // xName field hydrates it as `undefined`, which `.length` would throw on.
  const hasName = typeof me.xName === 'string' && me.xName.length > 0;
  const primaryName = hasName ? me.xName : `@${handle}`;
  // Stable avatar/hero seed — the real name feeds nicer fallback initials while
  // colour stays keyed to the immutable identity inside the wrappers.
  const seedName = me.xName ?? me.xHandle;
  const rankValue = me.rank !== null ? `#${String(me.rank)}` : '0';

  // Fixed-height identity card — the children are absolutely positioned, so the
  // explicit `h-52` is what gives the card its height.
  return (
    <Panel data-testid="profile-card" className="relative h-52 w-full overflow-hidden p-0">
      {/* Brand OG halftone fills the card — uniform brand-blue raster with the
          centred Morfi watermark (the `mono` shell background). `object-cover`
          scales it PROPORTIONALLY to fill the card (never squashed/stretched),
          zoomed in via `scale-125`; the card's `overflow-hidden` crops the rest. */}
      <Image
        src="/images/og/shells/mono.png"
        alt=""
        aria-hidden
        width={1200}
        height={630}
        unoptimized
        className="absolute inset-0 h-full w-full scale-140 object-cover"
      />

      <div className="absolute inset-0 flex flex-col justify-between p-4">
        {/* Top: Morfi wordmark (left) · share (right). */}
        <div className="flex items-start justify-between gap-2">
          <Image
            src={LOGO_SRC_DARK_BACKGROUND}
            alt="Morfi"
            width={logoWordmarkWidthForHeight(LOGO_HEIGHT_PX)}
            height={LOGO_HEIGHT_PX}
            className="block h-6 w-auto"
          />
          <ShareModal
            surface={{
              kind: 'rewards_rank',
              handle: me.xHandle ?? 'anon',
              rank: me.rank ?? 0,
              points: me.points.current,
              levelKey: me.level?.key ?? 'spectator',
              levelName: me.level?.name ?? '',
              avatar: me.avatar,
            }}
            url={shareUrl}
            text={t('text', { rank: me.rank ?? 0, points: me.points.current })}
            title={t('title')}
            trigger={
              <AnimatedButton
                variant="secondary"
                size="default"
                aria-label={tr('profile.share')}
                className="size-9 rounded-full border-white/30 bg-white/15 p-0 text-white backdrop-blur-sm hover:bg-white/25"
              >
                <Icon icon={Upload01Icon} className="size-4" />
              </AnimatedButton>
            }
          />
        </div>

        {/* Bottom: identity (left) · Rank/Points stat cards (right). */}
        <div className="flex items-end justify-between gap-3">
          {/* Centre the lone @handle against the avatar; bottom-align the taller
              name + handle stack so it reads as one block. */}
          <div className={cn('flex min-w-0 flex-1 gap-3', hasName ? 'items-end' : 'items-center')}>
            <div className="relative shrink-0">
              <UserAvatar
                userId={me.account.identity}
                displayName={seedName}
                avatarUrl={me.avatar}
                size="xl"
                // ALLOW-NSFW-FALSE: rewards /me model carries no moderation verdict for the X avatar
                nsfw={false}
                // ALLOW-RASTER-LITERAL: rewards /me model exposes no avatarPreferences; rasterise is opt-in (false)
                rasterise={false}
                className="ring-2 ring-white/40"
              />
              {me.level !== null ? (
                <RankEmblem
                  rankKey={me.level.key}
                  size={26}
                  label={me.level.name}
                  className="absolute -right-1.5 -bottom-1.5 drop-shadow-md"
                />
              ) : null}
            </div>
            <div className="flex min-w-0 flex-col gap-1 pb-0.5">
              <span className="truncate text-body font-medium text-white">{primaryName}</span>
              {hasName && me.xHandle ? (
                <span className="truncate text-caption text-white/70">@{me.xHandle}</span>
              ) : null}
              <ProfileStatus
                created={me.account.created}
                frozen={me.frozen}
                creating={creating}
                onCreateAccount={onCreateAccount}
              />
            </div>
          </div>

          <div className="flex min-w-28 shrink-0 flex-col items-stretch gap-2">
            <StatCard
              label={tr('profile.rank')}
              value={rankValue}
              onPress={onOpenLevelLadder}
              pressLabel={tr('profile.viewLadder')}
              testId="level-ladder-open-btn"
              trailing={
                <span className="flex size-6 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white">
                  <Icon icon={ArrowUpRight01Icon} className="size-3.5" />
                </span>
              }
            />
            <StatCard
              label={tr('profile.points')}
              value={me.points.current}
              onPress={onOpenLevelLadder}
              pressLabel={tr('profile.viewLadder')}
              trailing={
                <AnimatedProgressCircle
                  progress={me.level?.pct ?? 0}
                  diameter={20}
                  strokeWidth={2.5}
                  trackOpacity={0.25}
                  strokeClassName="stroke-white"
                  trackClassName="stroke-white"
                />
              }
            />
          </div>
        </div>
      </div>
    </Panel>
  );
}

/** Status slot under the identity — create-account CTA or frozen, else nothing. */
function ProfileStatus({
  created,
  frozen,
  creating,
  onCreateAccount,
}: {
  created: boolean;
  frozen: boolean;
  creating: boolean;
  onCreateAccount: () => void;
}): React.ReactNode {
  const tr = useTranslations('rewards');
  if (!created) {
    return (
      <AnimatedButton
        data-testid="create-account-btn"
        variant="primary"
        size="xs"
        disabled={creating}
        loading={creating}
        onClick={onCreateAccount}
        className="mt-0.5 w-fit"
      >
        {creating ? tr('profile.creating') : tr('profile.createAccount')}
      </AnimatedButton>
    );
  }
  if (frozen) {
    return (
      <span className="mt-0.5 inline-flex w-fit items-center rounded-pill border border-white/30 bg-feedback-error-accent/80 px-2 py-0.5 text-caption font-medium text-white backdrop-blur-sm">
        {tr('profile.frozen')}
      </span>
    );
  }
  return null;
}

/** Frosted key-value stat card with a bottom-right affordance slot. When
 *  `onPress` is set the WHOLE card is the press target (so the trailing slot must
 *  be a non-interactive adornment, not a nested button). */
function StatCard({
  label,
  value,
  trailing,
  onPress,
  pressLabel,
  testId,
}: {
  label: string;
  value: string;
  trailing: React.ReactNode;
  onPress?: (() => void) | undefined;
  pressLabel?: string | undefined;
  testId?: string | undefined;
}): React.ReactNode {
  const base =
    'relative w-full rounded-panel-inset border border-white/20 bg-white/15 px-3 py-2 pr-9 text-left backdrop-blur-sm';
  const body = (
    <>
      <span className="block text-micro font-medium uppercase tracking-[0.04em] text-white/70">
        {label}
      </span>
      <span className="block whitespace-nowrap text-body-lg font-bold tabular-nums text-white">
        {value}
      </span>
      <div className="absolute right-1.5 bottom-1.5 flex size-6 items-center justify-center">
        {trailing}
      </div>
    </>
  );
  if (onPress === undefined) {
    return <div className={base}>{body}</div>;
  }
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onPress}
      aria-label={pressLabel}
      className={cn(base, 'cursor-pointer transition-colors hover:bg-white/25')}
    >
      {body}
    </button>
  );
}

export { PrizeBanner, PrizeModal } from './prize-pool';
